import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, Skeleton } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import _ from 'lodash';

import httpClient from '../../utils/HttpClient';
import { PRODUCT_KNOWLEDGE_URL } from '../../constants/Url';
import { showFormatDate } from '../../utils/Utils';
import DynamicLayout from '../new/components/DynamicLayout';
import ArticleAudioPlayer from '../../components/audio/ArticleAudioPlayer';
import NotFoundPage from '../NotFoundPage';
import '../new/styles/NewsDetailPage.css';

export default function ProductKnowledgeDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchDetail = async () => {
      try {
        setLoading(true);
        const res = await httpClient
          .get(`${PRODUCT_KNOWLEDGE_URL}/${id}`)
          .then((r) => r.data)
          .catch((err) => ({ status: 500, message: err?.message }));

        if (!isMounted) return;

        if (res && res.status === 200 && res.data) {
          setData(res.data);
          setNotFound(false);
        } else {
          setNotFound(true);
        }
      } catch (err) {
        console.error('fetchDetail error:', err);
        if (isMounted) setNotFound(true);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (id) {
      fetchDetail();
    }

    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/product-knowledge');
    }
  };

  return (
    <div className="esi-std-container w-full">
      <div className="pt-6 pb-2">
        <div
          onClick={handleBack}
          className="inline-flex items-center gap-2 cursor-pointer text-gray-500 hover:text-black transition-colors font-medium text-sm sm:text-base"
        >
          <ArrowLeftOutlined className="text-base" />
          <span>{t('text_back_previous_page') || '返回上一页'}</span>
        </div>
      </div>

      {loading && (
        <div className="esi-std-white-space flex flex-col gap-3 w-full">
          <Skeleton.Image rootClassName="news-image w-full" className="news-image" active={true} />
          <Skeleton.Input active block />
          <Skeleton.Input active block />
          <Skeleton.Input active block />
          <Skeleton.Input active block />
          <div className="flex items-center justify-between">
            <Skeleton.Button className="news-primary-button" active shape="default" />
            <Skeleton.Input className="news-published-date" active />
          </div>

          <div className="flex flex-col gap-[4px]">
            <Skeleton.Input active block />
            <Skeleton.Input active block />
            <Skeleton.Input active block />
          </div>
        </div>
      )}

      {!loading && notFound ? (
        <NotFoundPage />
      ) : (
        !loading &&
        data && (
          <div style={{ marginTop: '20px', marginBottom: '40px' }}>
            <Card className="card-reponsive" style={{margin:"auto"}}>
              <div className="esi-std-white-space flex flex-col px-3 md:px-6 gap-6">
                {data?.coverimage && (
                  <img className="news-image" src={typeof data.coverimage === 'object' ? data.coverimage?.url : data.coverimage} alt={data.title} />
                )}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <h1 className="h1 flex-1 !mb-0">{data?.title}</h1>
                  <ArticleAudioPlayer
                    title={data?.title}
                    subtitle={data?.subtitle}
                    layouts={data?.layouts}
                    content={data?.content}
                  />
                </div>

                {data?.subtitle && <div className="news-summary">{data.subtitle}</div>}

                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="news-published-date flex items-center gap-2">
                    {data?.readingTime ? (
                      <span>
                        {t('text_read_time') || '阅读时长'}: {data.readingTime}{' '}
                        {t('text_minutes') || '分钟'}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="flex flex-col gap-[4px]">
                  {Array.isArray(data?.layouts) && data.layouts.length > 0 ? (
                    data.layouts.map((item, idx) => <DynamicLayout key={idx} data={item} />)
                  ) : (
                    <div className="py-8 text-center text-gray-400 text-sm">
                      {data?.content || ''}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>
        )
      )}

      <div className="esi-std-white-space"></div>
    </div>
  );
}
